import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';

import {
  AudioDeviceOption,
  MICROPHONE_DEVICES_CHANGED_EVENT,
  getMicrophoneDevices,
  normalizeMicrophoneLabel,
} from '@eluve/mic-utils';
import { useAnalytics } from '@eluve/posthog-react';

import {
  useSelectedMicrophoneDevice,
  useSelectedMicrophoneMeta,
} from '../useSelectedMicrophoneDevice';

/**
 * [ENG-5490] Minimum time between automatic preferred-device switch-backs.
 * A USB device cycling through power-save produces rapid appear/disappear
 * bursts; without a cooldown the selection would ping-pong on every cycle.
 */
export const AUTO_SWITCH_COOLDOWN_MS = 5_000;

export interface UseMicrophoneDeviceSelectorParams {
  getDevices?: () => Promise<AudioDeviceOption[]>;
  /**
   * Optional side-effect hook (toasts, machine events). Persistence is NOT
   * the caller's job: the hook writes `selectedMic` + `selectedMicMeta` to
   * localStorage itself so the two keys can never drift.
   */
  onSelectedDeviceChange?: (device: AudioDeviceOption | null) => void;
  selectedId: string | null;
  preferredMicrophoneNames?: string[];
  /**
   * [ENG-5490] Opt-in: when a strictly higher-priority preferred-list device
   * (re)appears and the current selection was NOT a manual user pick,
   * automatically re-select it. Callers must gate this on recording state —
   * never enable it while a recording is active.
   */
  autoSwitchToPreferred?: boolean;
}

export interface UseMicrophoneDeviceSelectorResult {
  options: AudioDeviceOption[];
  selectedMicrophone: AudioDeviceOption | undefined;
  handleValueChange: (deviceId: string) => void;
  handleOpenChange: (open: boolean) => void;
}

export const useMicrophoneDeviceSelector = ({
  getDevices = getMicrophoneDevices,
  onSelectedDeviceChange,
  selectedId,
  preferredMicrophoneNames,
  autoSwitchToPreferred = false,
}: UseMicrophoneDeviceSelectorParams): UseMicrophoneDeviceSelectorResult => {
  const analytics = useAnalytics('microphone-device-selector');

  const isLoading = useRef(false);
  const [options, setOptions] = useState<AudioDeviceOption[]>([]);
  const [, setSelectedMicId] = useSelectedMicrophoneDevice();
  const [selectedMicMeta, setSelectedMicMeta] = useSelectedMicrophoneMeta();
  const lastAutoSwitchAtRef = useRef(0);

  const selectedMicrophone = useMemo(
    () => options.find((option) => option.deviceId === selectedId),
    [options, selectedId],
  );

  // Single funnel for every selection change: BOTH localStorage keys
  // (`selectedMic` id + `selectedMicMeta` label/source) are written here so
  // they can never drift from the actual selection or from each other.
  // Consumers read `selectedMic` reactively via useSelectedMicrophoneDevice.
  // Clearing the selection deliberately KEEPS the meta: an unplugged mic's
  // stored identity is exactly what recovers it on replug.
  const selectDevice = useCallback(
    (device: AudioDeviceOption | null, source: 'manual' | 'auto') => {
      setSelectedMicId(device?.deviceId ?? null);
      if (device) {
        setSelectedMicMeta({ label: device.label, source });
      }
      onSelectedDeviceChange?.(device);
    },
    [onSelectedDeviceChange, setSelectedMicId, setSelectedMicMeta],
  );

  const fetchOptions = useCallback(async () => {
    if (isLoading.current) {
      return;
    }
    isLoading.current = true;
    const devices = await getDevices();
    setOptions(devices);
    isLoading.current = false;
  }, [getDevices]);

  useMount(() => fetchOptions());

  useEffect(() => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', fetchOptions);
    }

    window.addEventListener(MICROPHONE_DEVICES_CHANGED_EVENT, fetchOptions);

    return () => {
      if (navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          fetchOptions,
        );
      }
      window.removeEventListener(
        MICROPHONE_DEVICES_CHANGED_EVENT,
        fetchOptions,
      );
    };
  }, [fetchOptions]);

  useEffect(() => {
    if (isLoading.current) {
      return;
    }

    // Priority = index in the preferred-names list (case-insensitive
    // substring match, first hit wins); Infinity = not a preferred device.
    const priorityOf = (device: AudioDeviceOption): number => {
      if (!preferredMicrophoneNames?.length) {
        return Infinity;
      }
      const index = preferredMicrophoneNames.findIndex((name) =>
        device.label.toLowerCase().includes(name.toLowerCase()),
      );
      return index === -1 ? Infinity : index;
    };

    const canSelectDefault = !selectedId && options.length > 0;
    const shouldClearOldId = selectedId && !selectedMicrophone;

    if (canSelectDefault || shouldClearOldId) {
      // 1. [ENG-5490] Recover a MANUALLY picked device by label — a replug
      // (or Windows re-enumeration) can mint a new deviceId for the mic the
      // user explicitly chose. Applies both when the stored id dangles AND
      // when the selection was already cleared by a full unplug (meta
      // survives clearing for exactly this). Automatic selections skip this
      // and re-evaluate through the preferred chain below — a stale auto
      // identity must not outrank a preferred device. Empty labels never
      // match.
      if (selectedMicMeta?.label && selectedMicMeta.source === 'manual') {
        const storedLabel = normalizeMicrophoneLabel(selectedMicMeta.label);
        const labelMatch = storedLabel
          ? options.find(
              (device) =>
                normalizeMicrophoneLabel(device.label) === storedLabel,
            )
          : undefined;

        if (labelMatch) {
          analytics.capture('appointment.microphone_recovered_by_label', {
            audio_source: labelMatch.label,
          });
          selectDevice(labelMatch, selectedMicMeta.source);
          return;
        }
      }

      let selectedDevice = null;

      // 2. Try to find preferred device by name (if configured)
      // Loop through preferred names in order and find first match
      if (preferredMicrophoneNames && preferredMicrophoneNames.length > 0) {
        for (const preferredName of preferredMicrophoneNames) {
          selectedDevice = options.find((device) =>
            device.label.toLowerCase().includes(preferredName.toLowerCase()),
          );
          if (selectedDevice) {
            break;
          }
        }
      }

      // 3. Fall back to "default" device
      selectedDevice ??= options.find(
        (device) => device.deviceId === 'default',
      );

      // 4. Fall back to first available
      selectedDevice ??= options[0];

      selectDevice(selectedDevice ?? null, 'auto');
      return;
    }

    // [ENG-5490] Switch-back: the selection is valid, but a strictly
    // higher-priority preferred device is (back) in the list — e.g. the
    // clinic's USB mic was replugged after selection fell back to a laptop
    // mic. Never overrides a manual pick; cooldown absorbs flapping devices.
    if (
      autoSwitchToPreferred &&
      selectedMicrophone &&
      selectedMicMeta?.source !== 'manual'
    ) {
      const now = Date.now();
      if (now - lastAutoSwitchAtRef.current < AUTO_SWITCH_COOLDOWN_MS) {
        return;
      }

      const currentPriority = priorityOf(selectedMicrophone);
      const best = options.reduce<AudioDeviceOption | null>(
        (acc, device) =>
          priorityOf(device) < (acc ? priorityOf(acc) : Infinity)
            ? device
            : acc,
        null,
      );

      if (best && priorityOf(best) < currentPriority) {
        lastAutoSwitchAtRef.current = now;
        analytics.capture('appointment.microphone_auto_switched', {
          audio_source: best.label,
          previous_audio_source: selectedMicrophone.label,
        });
        selectDevice(best, 'auto');
      }
    }
  }, [
    analytics,
    autoSwitchToPreferred,
    selectDevice,
    options,
    selectedId,
    selectedMicrophone,
    selectedMicMeta,
    preferredMicrophoneNames,
  ]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      analytics.capture('appointment.audio_source_dropdown_clicked', {
        audio_source_count: options.length,
      });
    }
  };

  const handleValueChange = (deviceId: string) => {
    const device = options.find((option) => option.deviceId === deviceId);
    analytics.capture('appointment.audio_source_selected', {
      audio_source: device?.label ?? deviceId,
    });
    selectDevice(device ?? null, 'manual');
  };

  return {
    options,
    selectedMicrophone,
    handleValueChange,
    handleOpenChange,
  };
};
